import React from 'react';
import { Link } from 'react-router-dom';
import {
    Paper,
    Typography,
    Box,
    Chip,
    Stack,
    Divider
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Utils } from 'core/utils';

function ModernJobCard({ job }) {
    // Build location string properly
    const getLocation = () => {
        const parts = [];
        if (job.company?.city) {
            parts.push(job.company.city);
        }
        if (job.company?.country?.name) {
            parts.push(Utils.getDefaultName(job.company.country));
        }
        if (parts.length > 0) {
            return parts.join(', ');
        }
        if (job.address) {
            return job.address;
        }
        return 'Location not specified';
    };

    // Get salary display
    const getSalary = () => {
        if (job.salary) {
            return `£${job.salary}${job.pay_rate ? `/${job.pay_rate}` : ''}`;
        }
        if (job.pay_maximum) {
            return `£${job.pay_maximum}${job.pay_rate ? `/${job.pay_rate}` : ''}`;
        }
        return null;
    };

    return (
        <Paper
            component={Link}
            to={`/job/${job.id}`}
            elevation={0}
            sx={{
                p: 2,
                mb: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    transform: 'translateY(-2px)',
                    borderColor: 'primary.main'
                }
            }}
        >
            {/* Left side - Title, company, and info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography 
                        variant="subtitle1" 
                        fontWeight={600} 
                        sx={{ 
                            color: 'text.primary',
                            '&:hover': { color: 'primary.main' },
                            flexShrink: 0
                        }}
                    >
                        {Utils.normalizeText(job.title)}
                    </Typography>
                    {job.company?.title && (
                        <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {Utils.normalizeText(job.company.title)}
                        </Typography>
                    )}
                </Box>
                
                {/* All info in one line */}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5, alignItems: 'center' }}>
                    <Chip
                        icon={<LocationOnIcon sx={{ fontSize: '14px !important' }} />}
                        label={getLocation()}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '22px' }}
                    />
                    {job.jobtypes && job.jobtypes.length > 0 && (
                        <Chip
                            icon={<WorkIcon sx={{ fontSize: '14px !important' }} />}
                            label={Utils.normalizeText(job.jobtypes[0].title)}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.7rem', height: '22px' }}
                        />
                    )}
                    {job.schedules && job.schedules.length > 0 && (
                        <Chip
                            label={Utils.normalizeText(job.schedules[0].title)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '22px' }}
                        />
                    )}
                    {job.experience_level && (
                        <Chip
                            label={job.experience_level}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '22px' }}
                        />
                    )}
                    <Chip
                        icon={<AccessTimeIcon sx={{ fontSize: '14px !important' }} />}
                        label={Utils.timeAgo(job.updated_at)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: '22px' }}
                    />
                </Stack>
            </Box>

            {/* Right side - Salary */}
            {getSalary() && (
                <Typography 
                    variant="subtitle1" 
                    fontWeight={600} 
                    color="primary.main" 
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                    {getSalary()}
                </Typography>
            )}
        </Paper>
    );
}

export default ModernJobCard;

